/**
 * Esteira do portal na área do perito (E50.F6 — handoff da certificação).
 *
 * Renderizada na CasosPage SÓ para o escritório-plataforma (perfil
 * `is_platform`; a autorização real é do servidor — 403 fora da plataforma).
 * Autocontida de propósito: fetch, abas e tabela próprios, zero interferência
 * na listagem existente do perito (regressão zero por construção).
 *
 * Abas: "Certificações requeridas" (ordenadas pelo prazo mais urgente
 * no backend) e "Todos do portal". Cada linha mostra o escritório-cliente, o
 * nº de certificações requeridas e o prazo D-n (estouro em tinta de perigo,
 * sinalizado sobre o estado corrente).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Blueprint,
  Seg,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type SegOption,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { CaseError, listCases, type CaseRecordData, type PlatformEscopo } from '@/lib/cases';
import { diasAtePrazo } from '@/lib/sla';
import { useApiClient } from '@/lib/api';

function PrazoBadge({ slaDueAtIso, agora }: { slaDueAtIso: string; agora: Date }) {
  const dias = diasAtePrazo(slaDueAtIso, agora);
  const estourado = dias < 0;
  return (
    <span
      data-testid="esteira-prazo-badge"
      title={estourado ? ptBR.esteiraPortal.prazoEstourado : undefined}
      className={
        estourado
          ? 'border border-danger/30 bg-danger-50 px-2.5 py-[3px] font-mono text-[11px] tracking-[0.02em] text-danger-700'
          : 'border border-border bg-transparent px-2.5 py-[3px] font-mono text-[11px] tracking-[0.02em] text-fg'
      }
    >
      {estourado ? `D+${Math.abs(dias)}` : `D-${dias}`}
    </span>
  );
}

/**
 * O recorte é o `.seg` da prancha 1B — escolha exclusiva entre dois escopos.
 * Era `role="tab"` sem `tabpanel` correspondente; o `radiogroup` do primitivo
 * descreve o que o controle de fato faz e traz a navegação por setas.
 */
const ESCOPOS: readonly SegOption<PlatformEscopo>[] = [
  {
    value: 'certificacao_requerida',
    label: ptBR.esteiraPortal.tabFila,
    testId: 'esteira-tab-certificacao_requerida',
  },
  { value: 'clientes', label: ptBR.esteiraPortal.tabTodos, testId: 'esteira-tab-clientes' },
];

export interface EsteiraPortalProps {
  /**
   * Gate de exibição (perfil `is_platform`). O branch mora AQUI para a
   * CasosPage renderizar incondicionalmente (limite de complexidade dela);
   * a autorização real é do servidor.
   */
  visible: boolean;
}

export function EsteiraPortal({ visible }: EsteiraPortalProps) {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [escopo, setEscopo] = useState<PlatformEscopo>('certificacao_requerida');
  const [casos, setCasos] = useState<CaseRecordData[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    setCasos(null);
    setErro(null);
    listCases(apiClient, { escopo })
      .then((items) => {
        if (!cancelled) setCasos(items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setErro(
          err instanceof CaseError && err.reason === 'network'
            ? ptBR.errors.networkOffline
            : ptBR.esteiraPortal.erroCarregar,
        );
        setCasos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, escopo, visible]);

  if (!visible) return null;

  const agora = new Date();

  return (
    <Blueprint
      as="section"
      data-testid="esteira-portal"
      aria-labelledby="esteira-portal-title"
      className="mt-[26px] px-sc-6 pb-2 pt-sc-4"
    >
      <header className="mb-3.5 flex flex-wrap items-start justify-between gap-5">
        <div>
          <h2 id="esteira-portal-title" className="font-display text-heading-sm text-fg">
            {ptBR.esteiraPortal.title}
          </h2>
          <p className="text-body-sm text-fg-muted">{ptBR.esteiraPortal.subtitle}</p>
        </div>
        <Seg
          options={ESCOPOS}
          value={escopo}
          onChange={setEscopo}
          label={ptBR.esteiraPortal.title}
        />
      </header>

      {erro !== null ? (
        <p
          role="alert"
          data-testid="esteira-portal-erro"
          className="py-4 text-body-sm text-danger-700"
        >
          {erro}
        </p>
      ) : casos === null ? (
        <p data-testid="esteira-portal-loading" className="py-4 text-body-sm text-fg-muted">
          {ptBR.esteiraPortal.carregando}
        </p>
      ) : casos.length === 0 ? (
        <p data-testid="esteira-portal-vazio" className="py-4 text-body-sm text-fg-muted">
          {escopo === 'certificacao_requerida'
            ? ptBR.esteiraPortal.vazioFila
            : ptBR.esteiraPortal.vazioTodos}
        </p>
      ) : (
        <div className="overflow-x-auto [contain:layout]">
          <Table className="min-w-[42rem] text-left">
            <TableHeader>
              <TableRow>
                <TableHead>{ptBR.esteiraPortal.colCodigo}</TableHead>
                <TableHead>{ptBR.esteiraPortal.colEscritorio}</TableHead>
                <TableHead>{ptBR.esteiraPortal.colProcesso}</TableHead>
                <TableHead>{ptBR.esteiraPortal.colCaso}</TableHead>
                <TableHead>{ptBR.esteiraPortal.colCertificacoes}</TableHead>
                <TableHead>{ptBR.esteiraPortal.colPrazo}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {casos.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid="esteira-portal-row"
                  data-display-code={c.display_code}
                  className="cursor-pointer"
                  // E168.F7.C02 (GUS-180): o destino é o da lista (`/casos/:id`), pela
                  // modalidade e pelas peças — o caso eletrônico não cai no workspace.
                  onClick={() => navigate(`/casos/${c.id}`)}
                >
                  <TableCell className="font-mono text-fg">{c.display_code}</TableCell>
                  <TableCell className="text-fg">
                    {c.escritorio_nome ?? ptBR.casos.placeholderEmDash}
                  </TableCell>
                  <TableCell className="font-mono text-caption text-fg-muted">
                    {c.process_number ?? ptBR.casos.placeholderEmDash}
                  </TableCell>
                  <TableCell className="text-fg">{c.title}</TableCell>
                  <TableCell className="font-mono text-caption text-fg">
                    {c.n_certificacoes_requeridas ?? 0}
                  </TableCell>
                  <TableCell>
                    {c.certificacao_sla_due_at != null ? (
                      <PrazoBadge slaDueAtIso={c.certificacao_sla_due_at} agora={agora} />
                    ) : (
                      <span className="text-fg-muted">{ptBR.casos.placeholderEmDash}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Blueprint>
  );
}
